import React, { useEffect, useState } from 'react';
import { channelsApi, channelAuditApi, settingsApi } from '../lib/api';
import { channelDeepLink } from '../lib/deepLinks';
import { Plus, Pencil, Trash2, Tv, ExternalLink, AlertCircle, Bot, Search, Ban, CheckCircle2, Copy, Check } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import { toast } from 'sonner';

function ChannelDeepLink({ channel, botUsername, copied, onCopy }) {
  if (botUsername === null) return null;
  const link = botUsername ? channelDeepLink(botUsername, channel.id) : '';
  if (!link) {
    return (
      <p className="text-xs text-amber-400/90 mt-2" data-testid={`channel-link-hint-${channel.id}`}>
        Укажите username бота в настройках, чтобы получить ссылку
      </p>
    );
  }
  return (
    <div className="mt-2 max-w-sm space-y-1.5">
      <p className="text-[10px] uppercase tracking-wide text-zinc-500">Ссылка на покупку</p>
      <code
        className="block text-xs bg-white/5 px-2 py-1 rounded font-mono text-zinc-300 break-all"
        data-testid={`channel-link-${channel.id}`}
      >
        {link}
      </code>
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="h-7"
        onClick={() => onCopy(channel.id, link)}
        data-testid={`copy-channel-link-${channel.id}`}
      >
        {copied ? <Check className="w-3 h-3 text-emerald-400" /> : <Copy className="w-3 h-3" />}
        Скопировать
      </Button>
    </div>
  );
}

const initialFormState = {
  name: '',
  telegram_id: '',
  description: '',
  invite_link: '',
};

export default function Channels() {
  const [channels, setChannels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingChannel, setEditingChannel] = useState(null);
  const [formData, setFormData] = useState(initialFormState);
  const [submitting, setSubmitting] = useState(false);

  // Audit modal state
  const [auditChannel, setAuditChannel] = useState(null);
  const [auditResult, setAuditResult] = useState(null);
  const [auditLoading, setAuditLoading] = useState(false);
  const [kickingIds, setKickingIds] = useState(new Set());
  const [kickAllLoading, setKickAllLoading] = useState(false);
  const [botUsername, setBotUsername] = useState(null);
  const [copiedId, setCopiedId] = useState('');

  const formatApiError = (err, fallback) => {
    const detail = err?.response?.data?.detail;
    if (typeof detail === 'string' && detail.trim()) return detail;
    if (Array.isArray(detail)) {
      return detail.map((d) => d?.msg || d?.message || JSON.stringify(d)).join('; ');
    }
    if (detail && typeof detail === 'object') {
      return detail.message || detail.msg || JSON.stringify(detail);
    }
    if (err?.code === 'ECONNABORTED' || /timeout/i.test(err?.message || '')) {
      return 'Таймаут аудита: слишком много пользователей для проверки за один запрос. Повторите попытку.';
    }
    if (!err?.response) {
      return 'Нет ответа от сервера (сеть, CORS или обрыв прокси по таймауту). Повторите попытку.';
    }
    if (err.response.status === 502 || err.response.status === 504) {
      return 'Сервер/прокси оборвал аудит по таймауту. Повторите попытку.';
    }
    return fallback;
  };

  const runAudit = async (channel) => {
    setAuditChannel(channel);
    setAuditResult(null);
    setAuditLoading(true);
    try {
      const res = await channelAuditApi.audit(channel.id);
      setAuditResult(res.data);
      if (res.data?.timed_out) {
        toast.warning(
          `Аудит частично завершён (проверено ${res.data.checked_users || 0}). Истёкшие подписки проверяются в первую очередь — повторите для продолжения.`
        );
      }
    } catch (err) {
      toast.error(formatApiError(err, 'Ошибка аудита'));
      setAuditChannel(null);
    } finally {
      setAuditLoading(false);
    }
  };

  const handleKickAllExpired = async () => {
    if (!auditChannel || !auditResult) return;
    const kickable = (auditResult.ghosts || []).filter((g) => g.kickable).length;
    if (kickable === 0) {
      toast.info('Нет призраков, которых можно кикнуть');
      return;
    }
    if (!window.confirm(`Кикнуть ${kickable} пользователей с истёкшей подпиской из канала «${auditChannel.name}»? Это действие нельзя отменить.`)) return;

    setKickAllLoading(true);
    try {
      const res = await channelAuditApi.kickAllExpired(auditChannel.id);
      const d = res.data || {};
      const kicked = d.kicks_retried ?? d.kicked ?? 0;
      const processed = d.expired_processed ?? kickable;
      toast.success(`Кикнуто: ${kicked} из ${processed}`);
      runAudit(auditChannel);
    } catch (err) {
      toast.error(formatApiError(err, 'Ошибка массового кика'));
    } finally {
      setKickAllLoading(false);
    }
  };

  const handleKickGhost = async (ghost) => {
    if (!auditChannel) return;
    if (!window.confirm(`Кикнуть пользователя ${ghost.username ? '@' + ghost.username : ghost.user_telegram_id} из канала?`)) return;
    setKickingIds((s) => new Set([...s, ghost.user_telegram_id]));
    try {
      const res = await channelAuditApi.kick(auditChannel.id, {
        user_telegram_id: ghost.user_telegram_id,
        subscription_id: ghost.db_sub_id || null,
      });
      if (res.data.success) {
        toast.success('Пользователь кикнут');
        setAuditResult((r) => ({
          ...r,
          ghosts: (r.ghosts || []).filter((g) => g.user_telegram_id !== ghost.user_telegram_id),
        }));
      } else {
        toast.error(`Не удалось: ${res.data.error || 'неизвестная ошибка'}`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Ошибка кика');
    } finally {
      setKickingIds((s) => {
        const n = new Set(s);
        n.delete(ghost.user_telegram_id);
        return n;
      });
    }
  };

  const fetchChannels = async () => {
    try {
      const res = await channelsApi.getAll();
      setChannels(res.data);
    } catch (error) {
      toast.error('Ошибка загрузки каналов');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchChannels();
    settingsApi.get()
      .then((res) => {
        const name = String(res.data?.bot_username || '').replace(/^@/, '').trim();
        setBotUsername(name);
      })
      .catch(() => setBotUsername(''));
  }, []);

  const copyLink = async (channelId, link) => {
    try {
      await navigator.clipboard.writeText(link);
      setCopiedId(channelId);
      toast.success('Ссылка скопирована');
      setTimeout(() => setCopiedId(''), 1500);
    } catch {
      toast.error('Не удалось скопировать');
    }
  };

  const openCreateModal = () => {
    setEditingChannel(null);
    setFormData(initialFormState);
    setModalOpen(true);
  };

  const openEditModal = (channel) => {
    setEditingChannel(channel);
    setFormData({
      name: channel.name,
      telegram_id: channel.telegram_id,
      description: channel.description || '',
      invite_link: channel.invite_link || '',
    });
    setModalOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      if (editingChannel) {
        await channelsApi.update(editingChannel.id, formData);
        toast.success('Канал обновлен');
      } else {
        await channelsApi.create(formData);
        toast.success('Канал создан');
      }
      setModalOpen(false);
      fetchChannels();
    } catch (error) {
      toast.error('Ошибка сохранения канала');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (channel) => {
    if (!window.confirm(`Удалить канал "${channel.name}"?`)) return;

    try {
      await channelsApi.delete(channel.id);
      toast.success('Канал удален');
      fetchChannels();
    } catch (error) {
      toast.error('Ошибка удаления канала');
    }
  };

  const handleToggleActive = async (channel) => {
    try {
      await channelsApi.update(channel.id, { is_active: !channel.is_active });
      fetchChannels();
      toast.success(channel.is_active ? 'Канал деактивирован' : 'Канал активирован');
    } catch (error) {
      toast.error('Ошибка изменения статуса');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div data-testid="channels-page" className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white mb-2">Каналы</h1>
          <p className="text-zinc-500">Управление Telegram каналами для подписок</p>
        </div>
        <Button 
          onClick={openCreateModal} 
          className="glow-primary"
          data-testid="add-channel-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Добавить канал
        </Button>
      </div>

      {/* Bot Admin Instructions */}
      <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-4">
        <div className="flex gap-3">
          <Bot className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
          <div className="text-sm">
            <p className="text-amber-400 font-medium mb-2">⚠️ Важно: Настройка прав бота</p>
            <p className="text-zinc-400 mb-2">
              Чтобы бот мог создавать пригласительные ссылки и управлять пользователями, добавьте его как администратора в каждый канал с правами:
            </p>
            <ul className="text-zinc-400 space-y-1 ml-4 list-disc">
              <li><strong>Приглашать пользователей по ссылке</strong> — для создания уникальных invite-ссылок</li>
              <li><strong>Блокировать пользователей</strong> — для автоматического удаления при истечении подписки</li>
            </ul>
            <p className="text-zinc-500 mt-2 text-xs">
              Без этих прав функции автоматического приглашения и удаления работать не будут.
            </p>
          </div>
        </div>
      </div>

      {channels.length > 0 ? (
        <div className="card">
          <table className="data-table data-table-responsive">
            <thead>
              <tr>
                <th>Канал</th>
                <th>Telegram ID</th>
                <th>Описание</th>
                <th>Статус</th>
                <th className="text-right">Действия</th>
              </tr>
            </thead>
            <tbody>
              {channels.map((channel) => (
                <tr key={channel.id} data-testid={`channel-row-${channel.id}`}>
                  <td data-label="Канал">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-[#2AABEE]/10 flex items-center justify-center">
                        <Tv className="w-5 h-5 text-[#2AABEE]" />
                      </div>
                      <div className="min-w-0">
                        <p className="font-medium text-white">{channel.name}</p>
                        {channel.invite_link && (
                          <a 
                            href={channel.invite_link} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-xs text-[#2AABEE] hover:underline flex items-center gap-1"
                          >
                            Открыть <ExternalLink className="w-3 h-3" />
                          </a>
                        )}
                        <ChannelDeepLink
                          channel={channel}
                          botUsername={botUsername}
                          copied={copiedId === channel.id}
                          onCopy={copyLink}
                        />
                      </div>
                    </div>
                  </td>
                  <td data-label="Telegram ID">
                    <code className="text-xs bg-white/5 px-2 py-1 rounded font-mono text-emerald-400">
                      {channel.telegram_id}
                    </code>
                  </td>
                  <td data-label="Описание" className="text-zinc-400 max-w-xs truncate">
                    {channel.description || '—'}
                  </td>
                  <td data-label="Статус">
                    <Switch
                      checked={channel.is_active}
                      onCheckedChange={() => handleToggleActive(channel)}
                      data-testid={`channel-toggle-${channel.id}`}
                    />
                  </td>
                  <td data-label="Действия">
                    <div className="flex items-center justify-end gap-2">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => runAudit(channel)}
                        className="text-amber-400 hover:text-amber-300"
                        title="Аудит участников канала"
                        data-testid={`audit-channel-${channel.id}`}
                      >
                        <Search className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => openEditModal(channel)}
                        data-testid={`edit-channel-${channel.id}`}
                      >
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleDelete(channel)}
                        className="text-red-400 hover:text-red-300"
                        data-testid={`delete-channel-${channel.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="card">
          <div className="empty-state">
            <Tv className="empty-state-icon" />
            <p className="mb-4">Каналы не добавлены</p>
            <Button onClick={openCreateModal}>
              <Plus className="w-4 h-4 mr-2" />
              Добавить первый канал
            </Button>
          </div>
        </div>
      )}

      {/* Create/Edit Modal */}
      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="bg-[#0a0a0a] border-white/10">
          <DialogHeader>
            <DialogTitle>
              {editingChannel ? 'Редактировать канал' : 'Добавить канал'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="space-y-4">
              <div>
                <Label htmlFor="name">Название канала</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Мой приватный канал"
                  required
                  data-testid="channel-name-input"
                />
              </div>
              <div>
                <Label htmlFor="telegram_id">Telegram ID канала</Label>
                <Input
                  id="telegram_id"
                  value={formData.telegram_id}
                  onChange={(e) => setFormData({ ...formData, telegram_id: e.target.value })}
                  placeholder="-1001234567890"
                  required
                  data-testid="channel-telegram-id-input"
                />
                <p className="text-xs text-zinc-500 mt-1">
                  ID канала можно получить через @userinfobot
                </p>
              </div>
              <div>
                <Label htmlFor="description">Описание</Label>
                <Input
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Краткое описание канала"
                  data-testid="channel-description-input"
                />
              </div>
              <div>
                <Label htmlFor="invite_link">Ссылка-приглашение</Label>
                <Input
                  id="invite_link"
                  value={formData.invite_link}
                  onChange={(e) => setFormData({ ...formData, invite_link: e.target.value })}
                  placeholder="https://t.me/+..."
                  data-testid="channel-invite-link-input"
                />
              </div>
            </div>
            <DialogFooter className="mt-6">
              <Button type="button" variant="outline" onClick={() => setModalOpen(false)}>
                Отмена
              </Button>
              <Button type="submit" disabled={submitting} data-testid="save-channel-btn">
                {submitting ? 'Сохранение...' : 'Сохранить'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Audit Modal */}
      <Dialog
        open={!!auditChannel}
        onOpenChange={(o) => {
          if (!o) {
            setAuditChannel(null);
            setAuditResult(null);
          }
        }}
      >
        <DialogContent className="bg-[#0a0a0a] border-white/10 max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Search className="w-5 h-5 text-amber-400" />
              Аудит канала «{auditChannel?.name}»
            </DialogTitle>
          </DialogHeader>

          {auditLoading && (
            <div className="py-12 text-center">
              <div className="spinner mx-auto mb-3" />
              <p className="text-zinc-400 text-sm">
                Проверяем участников через Telegram... Это может занять до минуты
              </p>
            </div>
          )}

          {auditResult && !auditLoading && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div className="p-3 bg-zinc-900/50 rounded-lg border border-zinc-800">
                  <p className="text-xs text-zinc-500 mb-1">👥 В канале</p>
                  <p className="text-2xl font-bold text-white" data-testid="audit-real-count">
                    {auditResult.real_member_count ?? '?'}
                  </p>
                </div>
                <div className="p-3 bg-emerald-500/5 rounded-lg border border-emerald-500/20">
                  <p className="text-xs text-zinc-500 mb-1">✅ С активной подпиской</p>
                  <p className="text-2xl font-bold text-emerald-400">
                    {auditResult.valid_active ?? 0}
                  </p>
                </div>
                <div className="p-3 bg-red-500/5 rounded-lg border border-red-500/20">
                  <p className="text-xs text-zinc-500 mb-1">👻 Призраков</p>
                  <p className="text-2xl font-bold text-red-400" data-testid="audit-ghost-count">
                    {(auditResult.ghosts || []).length}
                  </p>
                </div>
                <div className="p-3 bg-zinc-900/50 rounded-lg border border-zinc-800">
                  <p className="text-xs text-zinc-500 mb-1">Проверено</p>
                  <p className="text-2xl font-bold text-white">
                    {auditResult.checked_users ?? (auditResult.ghosts || []).length}
                  </p>
                </div>
              </div>

              {auditResult.note && (
                <div className={`p-3 rounded-lg border text-xs flex gap-2 ${
                  auditResult.timed_out
                    ? 'bg-amber-500/5 border-amber-500/20 text-amber-200'
                    : 'bg-blue-500/5 border-blue-500/20 text-zinc-400'
                }`}>
                  <AlertCircle className={`w-4 h-4 flex-shrink-0 mt-0.5 ${
                    auditResult.timed_out ? 'text-amber-400' : 'text-blue-400'
                  }`} />
                  <p>{auditResult.note}</p>
                </div>
              )}

              {(auditResult.ghosts || []).length > 0 ? (
                <div>
                  <div className="flex items-center justify-between gap-2 mb-2 flex-wrap">
                    <h3 className="font-semibold text-white">
                      Найденные «призраки» ({auditResult.ghosts.length}):
                    </h3>
                    {auditResult.ghosts.some((g) => g.kickable) && (
                      <Button
                        size="sm"
                        onClick={handleKickAllExpired}
                        disabled={kickAllLoading}
                        className="bg-red-500/20 hover:bg-red-500/30 text-red-400 border border-red-500/30"
                        data-testid="kick-all-expired-btn"
                      >
                        <Ban className="w-3 h-3 mr-1" />
                        {kickAllLoading
                          ? 'Кикаем...'
                          : `Кикнуть всех (${auditResult.ghosts.filter((g) => g.kickable).length})`}
                      </Button>
                    )}
                  </div>
                  <div className="space-y-2">
                    {auditResult.ghosts.map((g) => {
                      const isKicking = kickingIds.has(g.user_telegram_id);
                      const memberStatus = g.member_status || g.telegram_status || 'unknown';
                      const subStatus = g.status || g.db_sub_status || 'expired';
                      const endDate = g.end_date || g.db_sub_end_date;
                      return (
                        <div
                          key={g.user_telegram_id}
                          className="flex items-center justify-between gap-3 p-3 bg-zinc-900/40 rounded-lg border border-zinc-800"
                          data-testid={`ghost-${g.user_telegram_id}`}
                        >
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1 flex-wrap">
                              <span className="font-mono text-sm text-white">
                                {g.username ? `@${g.username}` : g.user_telegram_id}
                              </span>
                              <span
                                className={`text-xs px-2 py-0.5 rounded-full ${
                                  memberStatus === 'administrator' || memberStatus === 'creator'
                                    ? 'bg-amber-500/20 text-amber-400'
                                    : 'bg-zinc-700 text-zinc-300'
                                }`}
                              >
                                {memberStatus}
                              </span>
                              <span className="text-xs px-2 py-0.5 rounded-full bg-red-500/20 text-red-400">
                                {subStatus}
                              </span>
                            </div>
                            {endDate && (
                              <p className="text-xs text-zinc-500">
                                {subStatus === 'cancelled' ? 'Подписка отменена. Действ. до:' : 'Подписка истекла:'}{' '}
                                {new Date(endDate).toLocaleString('ru-RU')}
                              </p>
                            )}
                          </div>
                          <Button
                            size="sm"
                            variant={g.kickable ? 'default' : 'outline'}
                            disabled={!g.kickable || isKicking}
                            onClick={() => handleKickGhost(g)}
                            className={
                              g.kickable
                                ? 'bg-red-500/20 hover:bg-red-500/30 text-red-400 border-red-500/30'
                                : ''
                            }
                            data-testid={`kick-ghost-${g.user_telegram_id}`}
                          >
                            <Ban className="w-3 h-3 mr-1" />
                            {isKicking ? '...' : 'Кикнуть'}
                          </Button>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ) : (
                <div className="p-4 bg-emerald-500/5 rounded-lg border border-emerald-500/20 flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5 text-emerald-400" />
                  <p className="text-zinc-300 text-sm">
                    Среди известных боту пользователей призраков не найдено
                  </p>
                </div>
              )}

              <div className="flex justify-end pt-2">
                <Button
                  variant="outline"
                  onClick={() => runAudit(auditChannel)}
                  size="sm"
                >
                  <Search className="w-3 h-3 mr-1" />
                  Перепроверить
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
